import { useState } from 'react';
import { t } from '@lingui/macro';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import _ from 'lodash';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import DataTable from '../../data-table/DataTable';
import { useQueryLoader } from '../../data-table/Loaders';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { SORT_ASC, useSuccessfulAction } from '../../../utilities/FormUtilities';
import { buildPageId } from '../AdminLinkUtils';
import { useColumnStyles } from './AgentStyles';
import { AGENT_DATA_SOURCE, AGENT_NAME_FIELD, AGENTS_FUNCTION_NAME, AGENT_STATUS_MOVE } from './AgentConstants';
import AgentListBulkEdit from './AgentListBulkEdit';
import AgentListBulkUrl from './AgentListBulkUrl';
import { useBulkActionWrapper, useFormSaveService } from '../../../services/FormSaveService';
import AgentListActionButtons from './AgentListActionButtons';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import {
    buildActionParams,
    collectDeleteErrors,
    getAgentListColumns,
    getAgentListColumnOverrides,
    usePostBulkActionHandler,
} from './AgentListHelper';
import AgentListAddButtons from './AgentListAddButtons';
import { AgentDownloadDialog } from './AgentDownloadDialog';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import useUiAlerts, { createAlertConfig, ERROR } from '../../../stores/AlertsStore';

const initialSort = [{ property: AGENT_NAME_FIELD, direction: SORT_ASC }];

const refreshListInterval = 20000;
const functionName = AGENTS_FUNCTION_NAME;

export default function AgentAdminList() {
    const classes = useColumnStyles();
    const { deleteWrapper } = useFormDeleteWrapper();
    const formDeleteService = useFormDeleteService();
    const { bulkActionWrapper } = useBulkActionWrapper(t`Tagged Successfully`);
    const { bulkActionWrapper: bulkUrlActionWrapper } = useBulkActionWrapper(t`Moved Successfully`);
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [tableState, setTableState] = useState(undefined);
    const [tableSelection, setTableSelection] = useState(undefined);
    const [downloadAgentOpen, setDownloadAgentOpen] = useState(false);
    const [allowPageRefresh, setAllowPageRefresh] = useState(true);
    const formSaveService = useFormSaveService();
    const handleSuccessfulDelete = useSuccessfulAction();
    const [, uiAlertActions] = useUiAlerts();
    const postBulkActionHandler = usePostBulkActionHandler();
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger({
        interval: allowPageRefresh ? refreshListInterval : -1,
    });

    // Used by the data table to load data.
    const loader = useQueryLoader(AGENT_DATA_SOURCE, { disableFilterState: true });

    const postDeleteHandler = (response) => {
        const { results } = response;
        const deleteErrors = _.chain(results).filter({ deleted: false }).map('error').map('details').value();

        if (_.isEmpty(deleteErrors)) {
            return handleSuccessfulDelete(response, null, refresher);
        }

        uiAlertActions.createAlert(createAlertConfig(ERROR, collectDeleteErrors(deleteErrors).join('  ')));

        refresher();
    };

    const handleConfirmedDelete = () => {
        return deleteWrapper({
            deleteFunction: () =>
                formDeleteService.deleteSelection(
                    `${AGENT_DATA_SOURCE.crudUrl}/selection/softDelete`,
                    buildActionParams(AGENT_DATA_SOURCE, tableState),
                    tableSelection
                ),
            refresherFunc: refresher,
            postDeleteHandler,
        })();
    };

    const handleConfirmedBulkEdit = (bulkChanges) => {
        const taggedStatus = 'tagged';
        return bulkActionWrapper({
            bulkActionFunction: () =>
                formSaveService.bulkActionSelection(
                    `${AGENT_DATA_SOURCE.crudUrl}/updateSelection`,
                    buildActionParams(AGENT_DATA_SOURCE, tableState),
                    tableSelection,
                    { updateObject: bulkChanges },
                    _.partial(postBulkActionHandler, { status: taggedStatus })
                ),
            refresherFunc: refresher,
        })();
    };

    const handleConfirmedBulkUrl = (bulkChanges) => {
        const movedStatus = 'moved';
        const actionParams = buildActionParams(AGENT_DATA_SOURCE, tableState);
        const updateUrl = `${AGENT_DATA_SOURCE.crudUrl}/updateSelection`;
        const transformedBulkChanges = {
            serverUrl: bulkChanges.serverUrl,
            status: AGENT_STATUS_MOVE,
        };

        return bulkUrlActionWrapper({
            bulkActionFunction: () =>
                formSaveService.bulkActionSelection(
                    updateUrl,
                    actionParams,
                    tableSelection,
                    { updateObject: transformedBulkChanges },
                    _.partial(postBulkActionHandler, { status: movedStatus })
                ),
            refresherFunc: refresher,
        })();
    };

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setActionsDisabled(
            !currentTableSelection || (_.isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
    };

    const handleDownloadClick = () => {
        setDownloadAgentOpen(true);
    };

    return (
        <AdminPage
            title={t`nPoints`}
            pageId={buildPageId(AGENTS_FUNCTION_NAME)}
            actions={
                <Actions>
                    <FormControlLabel
                        control={
                            <Switch
                                onChange={(e) => {
                                    setAllowPageRefresh(e.target.checked);
                                }}
                                checked={allowPageRefresh}
                            />
                        }
                        label="Auto-Refresh"
                    />
                    <ConfirmDeleteButton
                        disabled={actionsDisabled}
                        handleConfirmedDelete={handleConfirmedDelete}
                        plural={tableSelection?.selection?.length > 1}
                    />
                    <AgentListBulkEdit
                        onConfirm={handleConfirmedBulkEdit}
                        disabled={actionsDisabled}
                        tableSelection={tableSelection}
                        tableSearch={tableState?.search}
                        searchFields={AGENT_DATA_SOURCE?.params?.searchFields}
                    />
                    <AgentListBulkUrl
                        onConfirm={handleConfirmedBulkUrl}
                        disabled={actionsDisabled}
                        tableSelection={tableSelection}
                        tableSearch={tableState?.search}
                        searchFields={AGENT_DATA_SOURCE?.params?.searchFields}
                    />
                    <AgentListActionButtons
                        actionsDisabled={actionsDisabled}
                        tableSelection={tableSelection}
                        dataSource={AGENT_DATA_SOURCE}
                        refresherFunc={refresher}
                        tableState={tableState}
                    />
                    <AgentListAddButtons handleDownloadClickFunc={handleDownloadClick} />
                </Actions>
            }
        >
            <>
                <AgentDownloadDialog open={downloadAgentOpen} setOpen={setDownloadAgentOpen} />
                <DataTable
                    tableId={functionName}
                    loader={loader}
                    triggerRef={triggerRef}
                    columns={getAgentListColumns(classes)}
                    visibleRows={0}
                    showSelectionColumn
                    sorters={initialSort}
                    onStateChanged={setTableState}
                    selectionOptions={{
                        mode: SELECTIONMODE_MULTI,
                        allowSelectAll: true,
                        idMapping: '_id',
                        onChange: handleTableSelectionChange,
                    }}
                    columnOverrides={getAgentListColumnOverrides}
                />
            </>
        </AdminPage>
    );
}
